import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../api/api-response';

interface AuthTokenDto {
  name?: string;
  token: string;
  tokenType: string;
  expiresAt: string;
  expiresIn: number;
}

const TOKEN_KEY = 'simcarecupons.token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  readonly token = signal<string | null>(localStorage.getItem(TOKEN_KEY));

  readonly role = computed<string | null>(() => {
    const tk = this.token();
    if (!tk) return null;
    try {
      const payload = JSON.parse(this.b64UrlDecode(tk.split('.')[1] ?? ''));
      return payload['role']
          ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
          ?? null;
    } catch {
      return null;
    }
  });

  // Resultado AO VIVO de GET /Usuarios/me (fonte da verdade = CP_USUARIO_ADMIN).
  // null = ainda nao consultado; nesse caso cai no papel do JWT (pode estar ~4h defasado).
  private readonly liveAdmin = signal<boolean | null>(null);

  readonly isAdmin = computed<boolean>(() => {
    const live = this.liveAdmin();
    return live !== null ? live : this.role() === 'ADMIN';
  });

  /** Reconsulta o backend se o usuario ainda e admin e atualiza o estado vivo. */
  refreshRole(): Observable<boolean> {
    return this.http
      .get<ApiResponse<{ nmUsuario: string; isAdmin: boolean }>>(`${environment.apiUrl}/Usuarios/me`)
      .pipe(
        tap(res => { if (res?.data) this.liveAdmin.set(!!res.data.isAdmin); }),
        map(res => !!res?.data?.isAdmin),
        catchError(() => of(this.role() === 'ADMIN')),
      );
  }

  readonly username = computed<string | null>(() => {
    const tk = this.token();
    if (!tk) return null;
    try {
      const payload = JSON.parse(this.b64UrlDecode(tk.split('.')[1] ?? ''));
      return payload['Username'] ?? payload['unique_name'] ?? payload['name'] ?? null;
    } catch {
      return null;
    }
  });

  private b64UrlDecode(seg: string): string {
    const pad = seg.length % 4 === 0 ? '' : '='.repeat(4 - (seg.length % 4));
    const b64 = (seg + pad).replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(
      atob(b64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
  }

  isAuthenticated(): boolean {
    return !!this.token();
  }

  login(login: string, password: string): Observable<ApiResponse<AuthTokenDto>> {
    return this.http
      .post<ApiResponse<AuthTokenDto>>(`${environment.apiUrl}/Auth`, { login, password })
      .pipe(
        tap(res => {
          const tk = res?.data?.token;
          if (tk) {
            localStorage.setItem(TOKEN_KEY, tk);
            this.token.set(tk);
          }
        }),
      );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.token.set(null);
    this.liveAdmin.set(null);
  }
}
